import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { AsDiv, TitleXS } from '@/atoms/Text'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Modal } from '@/molecules/Modal'
import { FeaturedProjectContentCollection, Maybe } from '@/types/codegen-contentful'
import { GuildRolesQuery } from '@/types/codegen-federation'
import { buildAnalyticsLink } from '@/utils/AnalyticsLinkBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

interface FeaturedProjectWatchButtonGroupProps {
  campaign?: Maybe<string>
  contentCollection?: Maybe<FeaturedProjectContentCollection>
  href?: Maybe<string>
  guildMemberHref?: Maybe<string>
  data?: GuildRolesQuery
  showDavidVideo?: boolean
}

export const FeaturedProjectWatchButtonGroup: React.FC<FeaturedProjectWatchButtonGroupProps> = ({
  campaign,
  contentCollection,
  href,
  showDavidVideo = false,
}) => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const { asPath } = useSafeRouter()
  const [showModal, setShowModal] = useState(false)
  const analyticsLink = buildAnalyticsLink({
    href: href as string,
    path: asPath,
    campaign: campaign as string,
    linkContext: 'featured-section',
    source: 'featured-section',
  })

  const video = useMemo(() => {
    const content = contentCollection?.items?.[0]
    return {
      title: content?.title ?? 'Trailer',
      url: content?.source ?? '',
    }
  }, [contentCollection?.items])

  const handleLinkClick = () => {
    track('Featured Project Watch Now Link Clicked', { href })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()
    track('Featured Project Watch Trailer Clicked', { href })
    setShowModal(true)
  }

  const showLink = Boolean(analyticsLink)
  const showVideo = Boolean(video?.url)
  const primaryIconColor = showDavidVideo ? 'white' : 'current'

  return (
    <div className="flex w-full flex-col justify-center gap-4 md:flex-row">
      <When condition={showLink}>
        <LinkButton
          href={analyticsLink}
          className={classNames('h-12 w-full min-w-[220px] justify-center space-x-2 md:w-fit relative', {
            'text-white !bg-gradient-to-tl !from-[#3B60BA] !to-[#FBA349] !border-transparent !shadow-dark-1 hover:!from-[#3B60BA] hover:!to-[#FBA349]':
              showDavidVideo,
          })}
          onClick={handleLinkClick}
          variant="white"
        >
          {showDavidVideo && <div className="absolute inset-0 rounded-[inherit] bg-black/25" aria-hidden="true" />}
          <span className="relative z-10 flex items-center space-x-2">
            <PlayIcon size={24} color={primaryIconColor} />
            <TitleXS as={AsDiv}>{t('watchNow', 'Watch Now')}</TitleXS>
          </span>
        </LinkButton>
      </When>
      <When condition={showVideo}>
        <Button
          className="h-12 w-full min-w-[220px] justify-center space-x-2 !border-transparent bg-white/20 backdrop-blur-xl hover:bg-white/35 hover:text-white md:w-fit"
          variant="white"
          onClick={handleClick}
          outline
        >
          <PlayIcon size={24} color="current" />
          <TitleXS>{t('watchTrailer', 'Watch Trailer')}</TitleXS>
        </Button>
      </When>
      <Modal
        className="z-[99999]"
        panelClassesOverride="inline-block w-3/4 xl:w-full max-w-7xl align-middle shadow-xl rounded-2xl transition-all transform overflow-hidden"
        childWrapperClassName="mt-0"
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      >
        <EmbeddedVideo className="w-full rounded-2xl" {...video} />
      </Modal>
    </div>
  )
}
